import { Minus, Plus } from "lucide-react";

import { Button } from "@/components/Button";
import { Input } from "@/components/Input";

const STEPS = [-10, -5, -1, 1, 5, 10];

/**
 * Quick Adjust (TICKET FE-014, Master Context §10).
 *
 * The steps the spec asks for, plus an exact value. Clamped at zero because a
 * negative count is not a thing anyone means to type — the backend refuses it
 * anyway, but bouncing it back as an error would be a worse way to say so.
 */
export function QuantityControl({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  function step(amount: number) {
    const current = Number.parseFloat(value || "0");
    const next = Math.max(0, (Number.isFinite(current) ? current : 0) + amount);
    // Trim the float artefact a step can introduce; the value stays a string all
    // the way to the server, which does the real arithmetic.
    onChange(String(Number(next.toFixed(4))));
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {STEPS.filter((amount) => amount < 0).map((amount) => (
        <Button
          key={amount}
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => step(amount)}
          aria-label={`Retirer ${Math.abs(amount)}`}
        >
          {amount === -1 ? <Minus className="size-3.5" aria-hidden /> : amount}
        </Button>
      ))}

      <Input
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Quantité exacte"
        className="tnum w-24 text-center text-base font-semibold"
      />

      {STEPS.filter((amount) => amount > 0).map((amount) => (
        <Button
          key={amount}
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => step(amount)}
          aria-label={`Ajouter ${amount}`}
        >
          {amount === 1 ? <Plus className="size-3.5" aria-hidden /> : `+${amount}`}
        </Button>
      ))}
    </div>
  );
}
